import { instance } from "apis/interceptor";
import { GLOBAL_MEETING_SETTING_VALUE } from "types/meeting-settings.types";
import { MeetingRoomSetting } from "uis/PolicyManage/MeetingRoomPolicyModal/types";

type ResponseType<T> = {
  success: boolean;
  message: string;
  statusCode?: number;
  data?: T;
};

type TTabletPolicyResponse = {
  success: boolean;
  message?: string;
  data?: TTabletPolicy;
};

type TTabletPolicy = {
  isNoShowEnabled: GLOBAL_MEETING_SETTING_VALUE;
  isAutoCheckInEnabled: GLOBAL_MEETING_SETTING_VALUE;
  isNoShowSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE;
  isAutoCheckOutEnabled: GLOBAL_MEETING_SETTING_VALUE;
  isAutoCheckOutSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE;
  autoCheckOutDetectionTime: number;
  isFinishNotiEnabled: GLOBAL_MEETING_SETTING_VALUE;
  finishNotiTime: number;
  isFinishNotiSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE;
  hasMeetingRoomUsedTablet: boolean;
};

//* 태블릿 정책 설정 조회 (일괄)
export const getTabletPolicy = async (): Promise<TTabletPolicyResponse> => {
  try {
    const res = await instance.get("/meeting-settings/tablet");
    return { success: true, data: res.data };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "권한이 없습니다." };
  }
};

//* 태블릿 정책 설정 - 자동 체크인 기능 활성화 수정 (일괄)
export const updateTabletAutoCheckIn = async (
  isAutoCheckInEnabled: GLOBAL_MEETING_SETTING_VALUE
): Promise<ResponseType<boolean>> => {
  try {
    const updateData = isAutoCheckInEnabled === GLOBAL_MEETING_SETTING_VALUE.ON ? true : false;
    const res = await instance.patch("/meeting-settings/tablet/check-in/auto", {
      isAutoCheckInEnabled: updateData,
    });
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 태블릿 자동 체크인 상세 설정 조회 (상세)
export const getTabletAutoCheckInDetail = async (): Promise<ResponseType<MeetingRoomSetting[]>> => {
  try {
    const res = await instance.get("/meeting-settings/tablet/check-in/auto/detail");
    return { success: true, data: res.data, message: "정상적으로 조회되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "권한이 없습니다." };
  }
};

//* 태블릿 자동 체크인 상세 설정 수정 (상세)
export const updateTabletAutoCheckInDetail = async (
  meetingRoomSettings: MeetingRoomSetting[]
): Promise<ResponseType<boolean>> => {
  try {
    const updatedMeetingRoomSettings = meetingRoomSettings.map((setting) => {
      return {
        id: setting.meetingRoom.id,
        isAutoCheckInEnabled: setting.isAutoCheckInEnabled,
      };
    });
    const res = await instance.patch("/meeting-settings/tablet/check-in/auto/detail", updatedMeetingRoomSettings);
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 태블릿 자동 체크인 소리 알림 상세 설정 조회 (상세)
export const getTabletAutoCheckInSoundAlertDetail = async (): Promise<ResponseType<MeetingRoomSetting[]>> => {
  try {
    const res = await instance.get("/meeting-settings/tablet/check-in/sound-alert/detail");

    return { success: true, data: res.data, message: "정상적으로 조회되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "권한이 없습니다." };
  }
};

//* 태블릿 자동 체크인 소리 알림 상세 설정 수정 (상세)
export const updateTabletAutoCheckInSoundAlertDetail = async (
  meetingRoomSettings: MeetingRoomSetting[]
): Promise<ResponseType<boolean>> => {
  try {
    const updatedMeetingRoomSettings = meetingRoomSettings.map((setting) => {
      return {
        id: setting.meetingRoom.id,
        isNoShowSoundAlertEnabled: setting.isNoShowSoundAlertEnabled,
      };
    });
    const res = await instance.patch(
      "/meeting-settings/tablet/check-in/sound-alert/detail",
      updatedMeetingRoomSettings
    );
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 노쇼 취소 전 소리 알림 설정 수정 (일괄)
export const updateNoShowWarningSoundAlert = async (
  isNoShowSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE
): Promise<ResponseType<boolean>> => {
  try {
    const updateData = isNoShowSoundAlertEnabled === GLOBAL_MEETING_SETTING_VALUE.ON ? true : false;
    const res = await instance.patch("/meeting-settings/tablet/check-in/sound-alert", {
      isNoShowSoundAlertEnabled: updateData,
    });
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 자동 체크아웃 활성화 수정 (일괄)
export const updateAutoCheckOut = async (
  isAutoCheckOutEnabled: GLOBAL_MEETING_SETTING_VALUE
): Promise<ResponseType<boolean>> => {
  const updateData = isAutoCheckOutEnabled === GLOBAL_MEETING_SETTING_VALUE.ON ? true : false;
  try {
    const res = await instance.patch("/meeting-settings/tablet/auto-check-out", {
      isAutoCheckOutEnabled: updateData,
    });
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 자동 체크아웃 기능 상세 설정 조회 (상세)
export const getTabletAutoCheckOutDetail = async (): Promise<ResponseType<MeetingRoomSetting[]>> => {
  try {
    const res = await instance.get("/meeting-settings/tablet/auto-check-out/detail");
    return { success: true, data: res.data, message: "정상적으로 조회되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "권한이 없습니다." };
  }
};

//* 자동 체크아웃 - 음성 감지 시간 설정 (일괄)
export const updateAutoCheckOutDetectionTime = async (
  autoCheckOutDetectionTime: number
): Promise<ResponseType<boolean>> => {
  try {
    const res = await instance.patch("/meeting-settings/tablet/auto-check-out/time", {
      autoCheckOutDetectionTime: autoCheckOutDetectionTime,
    });
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 자동 체크아웃 전 소리 알림 활성화 (일괄)
export const updateAutoCheckOutSoundAlert = async (
  iAutoCheckOutSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE
): Promise<ResponseType<boolean>> => {
  try {
    const updateData = iAutoCheckOutSoundAlertEnabled === GLOBAL_MEETING_SETTING_VALUE.ON ? true : false;
    const res = await instance.patch("/meeting-settings/tablet/auto-check-out/sound-alert", {
      isAutoCheckOutSoundAlertEnabled: updateData,
    });
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 자동 체크아웃 소리 알림 상세 설정 수정 (상세)
export const updateTabletAutoCheckOutDetail = async (
  meetingRoomSetting: MeetingRoomSetting[]
): Promise<ResponseType<boolean>> => {
  try {
    const updatedMeetingRoomSettings = meetingRoomSetting.map((setting) => {
      return {
        id: setting.meetingRoom.id,
        isAutoCheckOutEnabled: setting.isAutoCheckOutEnabled,
        isAutoCheckOutSoundAlertEnabled: setting.isAutoCheckOutSoundAlertEnabled,
        autoCheckOutDetectionTime: setting.autoCheckOutDetectionTime,
      };
    });
    const res = await instance.patch("/meeting-settings/tablet/auto-check-out/detail", updatedMeetingRoomSettings);
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 회의 종료 안내 활성화 여부 변경 (일괄)
export const updateMeetingFinishAlert = async (
  isMeetingFinishAlertEnabled: GLOBAL_MEETING_SETTING_VALUE
): Promise<ResponseType<boolean>> => {
  try {
    const updateData = isMeetingFinishAlertEnabled === GLOBAL_MEETING_SETTING_VALUE.ON ? true : false;
    const res = await instance.patch("/meeting-settings/tablet/finish-noti", {
      isFinishNotiEnabled: updateData,
    });
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 회의 종료 안내 - 안내 알림 시간 설정 (일괄)
export const updateMeetingFinishAlertTime = async (meetingFinishAlertTime: number): Promise<ResponseType<boolean>> => {
  try {
    const res = await instance.patch("/meeting-settings/tablet/finish-noti/time", {
      finishNotiTime: meetingFinishAlertTime,
    });
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 회의 종료 안내 소리 활성화 (일괄)
export const updateMeetingFinishSoundAlert = async (
  isMeetingFinishSoundAlertEnabled: GLOBAL_MEETING_SETTING_VALUE
): Promise<ResponseType<boolean>> => {
  try {
    const updateData = isMeetingFinishSoundAlertEnabled === GLOBAL_MEETING_SETTING_VALUE.ON ? true : false;
    const res = await instance.patch("/meeting-settings/tablet/finish-noti/sound-alert", {
      isFinishNotiSoundAlertEnabled: updateData,
    });
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};

//* 회의 종료 안내 기능 상세 설정 조회 (상세)
export const getTabletMeetingFinishAlertDetail = async (): Promise<ResponseType<MeetingRoomSetting[]>> => {
  try {
    const res = await instance.get("/meeting-settings/tablet/finish-noti/detail");
    return { success: true, data: res.data, message: "정상적으로 조회되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "권한이 없습니다." };
  }
};

//* 회의 종료 안내 기능 상세 설정 수정 (상세)
export const updateTabletMeetingFinishAlertDetail = async (
  meetingRoomSetting: MeetingRoomSetting[]
): Promise<ResponseType<boolean>> => {
  try {
    const updatedMeetingRoomSettings = meetingRoomSetting.map((setting) => {
      return {
        id: setting.meetingRoom.id,
        isFinishNotiEnabled: setting.isFinishNotiEnabled,
        finishNotiTime: setting.finishNotiTime,
        isFinishNotiSoundAlertEnabled: setting.isFinishNotiSoundAlertEnabled,
      };
    });
    const res = await instance.patch("/meeting-settings/tablet/finish-noti/detail", updatedMeetingRoomSettings);
    return { success: true, data: res.data.result, message: "변경사항이 성공적으로 저장되었습니다." };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return { success: false, message: "권한이 없습니다." };
    } else return { success: false, message: "변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요." };
  }
};
